import { addDays, isEqual, subDays, subMonths, subWeeks, subYears } from 'date-fns'
import snakeCase from 'lodash/snakeCase'

import {
  AirbnbQualityStandardState,
  Granularity,
} from '@avantstay/arriere-clients/dist/arriereBackoffice'
import {
  dateTo12MonthsBefore,
  formatDate,
  getPeriodFromDatesType,
  parseISODate,
} from '@avantstay/backoffice-core'

import { ChartsComparison } from '@new/__global/components/IndicatorsFilter/utils'
import { Period } from '@new/domains/dashboard/screens/overview/_types'

import { AirbnbTripIssuesData } from '@arriere/types/Dashboard.types'
import type { MarketRatingsProps } from '@arriere/types/Regions.types'

const granularityFunctions = {
  [Granularity.daily]: (date: string) => formatDate(subDays(parseISODate(date), 1)),
  [Granularity.weekly]: (date: string) => formatDate(subWeeks(parseISODate(date), 1)),
  [Granularity.monthly]: (date: string) => formatDate(subMonths(parseISODate(date), 1)),
  [Granularity.quarterly]: (date: string) => formatDate(subMonths(parseISODate(date), 3)),
  [Granularity.yearly]: (date: string) => formatDate(subYears(parseISODate(date), 1)),
}

export const getComparisonPeriod = (
  period: getPeriodFromDatesType,
  granularity: string,
  comparison: ChartsComparison,
) => {
  const { fromDate, toDate } = period

  if (comparison === ChartsComparison.period) {
    const startDate = granularityFunctions[granularity as keyof typeof granularityFunctions](
      String(fromDate),
    )

    return {
      start: formatDate(startDate),
      end: formatDate(toDate),
    }
  }

  return {
    start: fromDate ? formatDate(dateTo12MonthsBefore(parseISODate(fromDate))) : '',
    end: toDate ? formatDate(dateTo12MonthsBefore(parseISODate(toDate))) : '',
  }
}

export const parsePeriodParam = (period?: Period) => {
  const fromDate = period ? parseISODate(period?.fromDate) : undefined
  const toDate = period ? parseISODate(period?.toDate) : undefined

  if (fromDate && toDate && isEqual(fromDate, toDate)) {
    return {
      fromDate,
      toDate: addDays(toDate, 1),
    }
  }

  return {
    fromDate,
    toDate,
  }
}

export const parseAirbnbTripIssuesData = (tripIssueData?: AirbnbTripIssuesData) => {
  return tripIssueData
    ? Object.keys(tripIssueData).map(listingFlagKey => {
        return {
          state: snakeCase(listingFlagKey).toUpperCase() as AirbnbQualityStandardState,
          count: tripIssueData[listingFlagKey as keyof typeof tripIssueData],
        }
      })
    : []
}

export const formatRatingData = (marketRatings: MarketRatingsProps) => {
  const { airbnb, bookingCom, expedia, vrbo } = marketRatings

  return {
    ratings: {
      homeAway: {
        overall: {
          avgStars: vrbo.avgOverall,
          totalRates: 0,
        },
        onlineListing: {
          avgStars: vrbo.avgOnlineListing,
        },
        checkIn: {
          avgStars: vrbo.avgCheckIn,
        },
        communication: {
          avgStars: vrbo.avgCommunication,
        },
        location: {
          avgStars: vrbo.avgLocation,
        },
        roomCleanliness: {
          avgStars: vrbo.avgRoomCleanliness,
        },
        conversionRate: vrbo.conversion.conversionRate,
        numberOfBookings: vrbo.conversion.numberOfBookings,
        numberOfReviews: vrbo.numberOfReviews,
      },
      airBnb: {
        overall: {
          avgStars: airbnb.avgOverall,
          totalRates: 0,
        },
        accuracy: {
          avgStars: airbnb.avgAccuracy,
        },
        checkIn: {
          avgStars: airbnb.avgCheckIn,
        },
        communication: {
          avgStars: airbnb.avgCommunication,
        },
        cleanliness: {
          avgStars: airbnb.avgCleanliness,
        },
        location: {
          avgStars: airbnb.avgLocation,
        },
        value: {
          avgStars: airbnb.avgValue,
        },
        conversionRate: airbnb.conversion.conversionRate,
        numberOfBookings: airbnb.conversion.numberOfBookings,
        numberOfReviews: airbnb.numberOfReviews,
      },
      bookingCom: {
        cleanliness: {
          avgStars: bookingCom.avgOverall,
        },
        location: {
          avgStars: bookingCom.avgLocation,
        },
        overall: {
          avgStars: bookingCom.avgPropertyCondition,
        },
        propertyCondition: {
          avgStars: bookingCom.avgPropertyCondition,
        },
        value: {
          avgStars: bookingCom.avgValue,
        },
        staff: {
          avgStars: bookingCom.avgStaff,
        },
        conversionRate: bookingCom.conversion.conversionRate,
        numberOfBookings: bookingCom.conversion.numberOfBookings,
        numberOfReviews: bookingCom.numberOfReviews,
      },
      expedia: {
        amenities: {
          avgStars: expedia.avgAmenities,
        },
        cleanliness: {
          avgStars: expedia.avgCleanliness,
        },
        communication: {
          avgStars: expedia.avgCommunication,
        },
        ecoFriendliness: {
          avgStars: expedia.avgEcoFriendliness,
        },
        overall: {
          avgStars: expedia.avgOverall,
        },
        propertyCondition: {
          avgStars: expedia.avgPropertyCondition,
        },
        conversionRate: expedia.conversion.conversionRate,
        numberOfBookings: expedia.conversion.numberOfBookings,
        numberOfReviews: expedia.numberOfReviews,
      },
    },
  }
}
